import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePortalIdentity } from "../auth/usePortalIdentity";
import { useAnalyticsConnections } from "../data/analyticsQueries";
import { SPEED_WORD, sourceShares } from "../data/dashboard";
import {
  KIND_GLOSSARY,
  cellOf,
  compare,
  comparisonWords,
  countWords,
  monthShortWord,
  monthWords,
  monthsBack,
  numberCell,
  pickerMonths,
  pointsWords,
  PROFILE_FIGURES,
  positionNumber,
  profileCell,
  positionWords,
  searchOf,
  siteCounts,
  stockholmMonth,
  visitsOf,
  type Cell,
  type Comparison,
  type CountKind,
  type MonthVisits,
  type SearchMonth,
  type SiteCounts
} from "../data/statistics";
import {
  ProfileReadFailure,
  useClientProfileMonths,
  useClientSearchMonths,
  useClientSiteCounts,
  useStatisticsMonths
} from "../data/statisticsQueries";
import glossary from "../lib/statisticsGlossary.json";
import { BoxState } from "../ui/BoxState";
import { ClientHead } from "../ui/ClientHead";
import { InfoHint } from "../ui/InfoHint";
import { MonthBars } from "../ui/MonthBars";
import { Panel } from "../ui/Panel";
import "./statistics/statistics.css";
import { useClientPath } from "../data/readSource";

/**
 * /statistics, "Statistik", build round 37 (2026-10-11). Decided by Mattej via Portal HQ on 2026-10-10
 * (Öppet O-024), superseding plan.md section 5.5's "there is no page of their own" (decision 0387),
 * whose rules for what is shown, aggregates only and retention still hold.
 *
 * Results first, then visits. Every figure is compared with the month before and with the same month
 * last year. One ⓘ per figure, from the shared glossary (app/lib/statisticsGlossary.json), and no
 * running text under the figures. No bounce rate, and no good or bad the portal invents: a drop is not
 * red, and only speed carries a judgement, Google's own. Top pages are kept only in the 24-hour
 * snapshot, never per month, so they stay on the overview.
 *
 * Build round 38 (2026-10-11): "Från Google" between the results and the visits, Search Console's
 * clicks, impressions and average position with the same two comparisons and the same states, and the
 * month's most common search words. Business Profile is not stored (its terms forbid keeping
 * aggregates).
 *
 * Build round 47 (2026-10-27): "Från Google-profilen" right after "Från Google", a live read since this
 * round, shown and never stored (decision 0614): views, taps on Ring, clicks to the website and
 * directions, with the same two comparisons. It is its own read per website and never holds up the
 * page: while it loads or fails, only its own section says so, and a website with no location has no
 * section at all.
 */

type Entry = { label: string; info: string };
const words = (glossary as { entries: Record<string, Entry> }).entries;

function entry(key: string): Entry {
  return words[key] ?? { label: key, info: "" };
}

function Label({ glossaryKey, testId }: { glossaryKey: string; testId?: string }) {
  const { label, info } = entry(glossaryKey);
  return (
    <span className="s-label">
      {label}
      <InfoHint label={label} text={info} testId={testId} />
    </span>
  );
}

/** A figure, or the state that stands in its place. */
function Figure({ cell, format = countWords }: { cell: Cell; format?: (value: number) => string }) {
  if (cell.state === "checking") {
    return (
      <span className="s-figure is-state" data-testid="stat-figure" data-state="checking">
        {entry("checking").label}
        <InfoHint label={entry("checking").label} text={entry("checking").info} />
      </span>
    );
  }
  if (cell.state === "not_measured") {
    return (
      <span className="s-figure is-state" data-testid="stat-figure" data-state="not_measured">
        {entry("not_measured").label}
        <InfoHint label={entry("not_measured").label} text={entry("not_measured").info} />
      </span>
    );
  }
  return (
    <span className="s-figure" data-testid="stat-figure" data-state="measured">
      {format(cell.value)}
    </span>
  );
}

/** The two comparisons under a figure; nothing while the figure is held for checking. */
function Comparisons({
  current,
  before,
  yearAgo,
  wordsFor = comparisonWords
}: {
  current: Cell;
  before: Cell;
  yearAgo: Cell;
  wordsFor?: (comparison: Comparison) => string;
}) {
  const lastMonth = compare(current, before);
  const lastYear = compare(current, yearAgo);
  if (lastMonth.state === "hidden") {
    return null;
  }
  return (
    <dl className="s-compare">
      <div data-testid="stat-vs-month">
        <dt>
          <Label glossaryKey="vs_last_month" />
        </dt>
        <dd>{wordsFor(lastMonth)}</dd>
      </div>
      <div data-testid="stat-vs-year">
        <dt>
          <Label glossaryKey="vs_last_year" />
        </dt>
        <dd>{wordsFor(lastYear)}</dd>
      </div>
    </dl>
  );
}

function ResultRow({ site, kind, month }: { site: SiteCounts; kind: CountKind; month: string }) {
  const key = KIND_GLOSSARY[kind];
  const current = cellOf(site, kind, month);
  const bars = Array.from({ length: 12 }, (_, index) => monthsBack(month, 11 - index)).map((m) => {
    const cell = cellOf(site, kind, m);
    const value = cell.state === "measured" ? cell.value : null;
    const spoken =
      cell.state === "measured"
        ? `${monthWords(m)}: ${countWords(cell.value)}`
        : cell.state === "checking"
          ? `${monthWords(m)}: kontrolleras`
          : `${monthWords(m)}: mäts inte`;
    return { month: m, short: monthShortWord(m), spoken, value };
  });
  return (
    <li className="s-row" data-testid="stat-row" data-kind={kind}>
      <div className="s-head">
        <Label glossaryKey={key} testId={`stat-info-${kind}`} />
        <Figure cell={current} />
      </div>
      <Comparisons current={current} before={cellOf(site, kind, monthsBack(month, 1))} yearAgo={cellOf(site, kind, monthsBack(month, 12))} />
      <MonthBars bars={bars} label={`${entry(key).label}, de tolv månaderna till och med ${monthWords(month)}`} barTestId="stat-bar" />
    </li>
  );
}

/** One quiet line: the month in progress, kind by kind. */
function SoFar({ site, now }: { site: SiteCounts; now: Date }) {
  const month = stockholmMonth(now);
  const parts = site.kinds.map((kind) => {
    const cell = cellOf(site, kind, month);
    const value =
      cell.state === "measured" ? countWords(cell.value) : cell.state === "checking" ? "kontrolleras" : "mäts inte";
    return `${entry(KIND_GLOSSARY[kind]).label} ${value}`;
  });
  return (
    <p className="s-sofar quiet" data-testid="stat-so-far">
      <Label glossaryKey="current_month" />
      <span>{parts.join(" · ")}</span>
    </p>
  );
}

function VisitRows({ rows, connectionId, month }: { rows: MonthVisits[]; connectionId: string | null; month: string }) {
  const clientPath = useClientPath();
  const now = visitsOf(rows, connectionId, month);
  const before = visitsOf(rows, connectionId, monthsBack(month, 1));
  const yearAgo = visitsOf(rows, connectionId, monthsBack(month, 12));
  const speedWord = (rating: string | null | undefined) => (rating ? (SPEED_WORD[rating] ?? null) : null);
  const shares = now?.sources ? sourceShares(now.sources) : [];
  return (
    <ul className="s-rows">
      <li className="s-row" data-testid="stat-visit-row" data-figure="visits">
        <div className="s-head">
          <Label glossaryKey="visits" testId="stat-info-visits" />
          <Figure cell={numberCell(now?.visits)} />
        </div>
        <Comparisons current={numberCell(now?.visits)} before={numberCell(before?.visits)} yearAgo={numberCell(yearAgo?.visits)} />
      </li>
      <li className="s-row" data-testid="stat-visit-row" data-figure="sources">
        <div className="s-head">
          <Label glossaryKey="sources" />
          {shares.length === 0 ? <Figure cell={{ state: "not_measured" }} /> : null}
        </div>
        {shares.length > 0 ? (
          <ul className="s-sources">
            {shares.map((share) => (
              <li key={share.label}>
                <span>{share.label}</span>
                <span className="s-share">{share.percent} %</span>
              </li>
            ))}
          </ul>
        ) : null}
      </li>
      <li className="s-row" data-testid="stat-visit-row" data-figure="mobile">
        <div className="s-head">
          <Label glossaryKey="mobile" />
          <Figure cell={numberCell(now?.mobile_share)} format={(value) => `${value} %`} />
        </div>
        <Comparisons
          current={numberCell(now?.mobile_share)}
          before={numberCell(before?.mobile_share)}
          yearAgo={numberCell(yearAgo?.mobile_share)}
          wordsFor={pointsWords}
        />
      </li>
      <li className="s-row" data-testid="stat-visit-row" data-figure="speed">
        <div className="s-head">
          <Label glossaryKey="speed" />
          {speedWord(now?.speed_rating) ? (
            <span className="s-figure" data-testid="stat-figure" data-state="measured">
              {speedWord(now?.speed_rating)}
            </span>
          ) : (
            <Figure cell={{ state: "not_measured" }} />
          )}
        </div>
        {speedWord(now?.speed_rating) ? (
          <dl className="s-compare">
            <div>
              <dt>
                <Label glossaryKey="vs_last_month" />
              </dt>
              <dd>{speedWord(before?.speed_rating) ?? "Mäts inte"}</dd>
            </div>
            <div>
              <dt>
                <Label glossaryKey="vs_last_year" />
              </dt>
              <dd>{speedWord(yearAgo?.speed_rating) ?? "Mäts inte"}</dd>
            </div>
          </dl>
        ) : null}
      </li>
      <li className="s-row" data-figure="pages">
        <div className="s-head">
          <Label glossaryKey="pages" />
          <Link to={clientPath("/overview")} data-testid="stat-pages-link">
            Finns på översikten
          </Link>
        </div>
      </li>
    </ul>
  );
}

/** "Från Google": Search Console for the month, the same states as the counts. */
function SearchRows({ rows, serviceId, month }: { rows: SearchMonth[]; serviceId: string; month: string }) {
  const now = searchOf(rows, serviceId, month);
  const before = searchOf(rows, serviceId, monthsBack(month, 1));
  const yearAgo = searchOf(rows, serviceId, monthsBack(month, 12));
  const figure = (key: "clicks" | "impressions", row: SearchMonth | null) => numberCell(row ? row[key] : null);
  // A measured month with no impressions has no position: there was nothing to average, which reads as
  // "Mäts inte" rather than as a place on the page.
  const position = (row: SearchMonth | null) => numberCell(row?.position ?? null);
  // Google ranks the words by clicks and can fill the ten with words nobody clicked; the glossary says
  // these are what people searched when they clicked here, so only a clicked word is shown.
  const searched = (now?.top_queries ?? []).filter((query) => query.clicks > 0);
  return (
    <ul className="s-rows">
      {(["clicks", "impressions"] as const).map((key) => (
        <li key={key} className="s-row" data-testid="stat-search-row" data-figure={`search_${key}`}>
          <div className="s-head">
            <Label glossaryKey={`search_${key}`} testId={`stat-info-search_${key}`} />
            <Figure cell={figure(key, now)} />
          </div>
          <Comparisons current={figure(key, now)} before={figure(key, before)} yearAgo={figure(key, yearAgo)} />
        </li>
      ))}
      <li className="s-row" data-testid="stat-search-row" data-figure="search_position">
        <div className="s-head">
          <Label glossaryKey="search_position" testId="stat-info-search_position" />
          <Figure cell={position(now)} format={positionNumber} />
        </div>
        <Comparisons current={position(now)} before={position(before)} yearAgo={position(yearAgo)} wordsFor={positionWords} />
      </li>
      <li className="s-row" data-testid="stat-search-row" data-figure="search_queries">
        <div className="s-head">
          <Label glossaryKey="search_queries" testId="stat-info-search_queries" />
          {!now ? <Figure cell={{ state: "not_measured" }} /> : null}
        </div>
        {now && searched.length > 0 ? (
          <ol className="s-sources" data-testid="stat-search-queries">
            {searched.map((query) => (
              <li key={query.key}>
                <span>{query.key}</span>
                <span className="s-share">{countWords(query.clicks)}</span>
              </li>
            ))}
          </ol>
        ) : null}
        {now && searched.length === 0 ? <p className="quiet">Inga sökord att visa den här månaden.</p> : null}
      </li>
    </ul>
  );
}

/**
 * The two sentences a failed live read can have: one for a broken connection, which opens Petrovic's
 * alert, and one for the rest. No connection at all hides the section instead (build round 53,
 * 2026-10-31, `useClientProfileMonths`): "Koppla från" opens no alert to promise.
 */
const PROFILE_RECONNECTING = new Set(["invalid_grant"]);

/** "Från Google-profilen": the live Business Profile read for the month, or nothing without a location. */
function ProfileSection({ serviceId, month }: { serviceId: string; month: string }) {
  const read = useClientProfileMonths(serviceId);

  // No location for this website: not a failure, and nothing to show.
  if (read.isSuccess && read.data === null) {
    return null;
  }

  let body: ReactNode;
  if (read.isPending) {
    body = (
      <p className="quiet" data-testid="stat-profile-loading">
        Hämtar siffrorna från Google.
      </p>
    );
  } else if (read.isError) {
    const code = read.error instanceof ProfileReadFailure ? read.error.code : "";
    body = (
      <p className="quiet" data-testid="stat-profile-failed">
        {PROFILE_RECONNECTING.has(code)
          ? "Siffrorna från Google-profilen kan inte hämtas just nu. Vi har fått en varning om det och kopplar om."
          : "Siffrorna från Google-profilen gick inte att hämta just nu. Ladda om sidan om en stund."}
      </p>
    );
  } else {
    const rows = read.data ?? [];
    body = (
      <ul className="s-rows">
        {PROFILE_FIGURES.map(({ figure, glossaryKey }) => {
          const current = profileCell(rows, figure, month);
          return (
            <li key={figure} className="s-row" data-testid="stat-profile-row" data-figure={glossaryKey}>
              <div className="s-head">
                <Label glossaryKey={glossaryKey} testId={`stat-info-${glossaryKey}`} />
                <Figure cell={current} />
              </div>
              <Comparisons
                current={current}
                before={profileCell(rows, figure, monthsBack(month, 1))}
                yearAgo={profileCell(rows, figure, monthsBack(month, 12))}
              />
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <section aria-label="Från Google-profilen" className="s-block" data-testid="stat-google-profile">
      <h3 className="s-block-title">Från Google-profilen</h3>
      {body}
    </section>
  );
}

function SiteSection({
  site,
  month,
  months,
  searches,
  connectionId,
  now
}: {
  site: SiteCounts;
  month: string;
  months: MonthVisits[];
  searches: SearchMonth[];
  connectionId: string | null;
  now: Date;
}) {
  const titleId = `stat-site-${site.serviceId}`;
  return (
    <Panel title={site.address} titleId={titleId} testId="stat-site">
      <section aria-label="Resultat" className="s-block">
        <h3 className="s-block-title">Resultat</h3>
        {site.kinds.length === 0 ? (
          <p className="quiet">Webbplatsen räknar inga resultat.</p>
        ) : (
          <ul className="s-rows">
            {site.kinds.map((kind) => (
              <ResultRow key={kind} site={site} kind={kind} month={month} />
            ))}
          </ul>
        )}
        {site.kinds.length > 0 ? <SoFar site={site} now={now} /> : null}
      </section>
      <section aria-label="Från Google" className="s-block" data-testid="stat-google">
        <h3 className="s-block-title">Från Google</h3>
        <SearchRows rows={searches} serviceId={site.serviceId} month={month} />
      </section>
      <ProfileSection serviceId={site.serviceId} month={month} />
      <section aria-label="Besök" className="s-block">
        <h3 className="s-block-title">Besök</h3>
        <VisitRows rows={months} connectionId={connectionId} month={month} />
      </section>
    </Panel>
  );
}

export function StatisticsRoute() {
  const identity = usePortalIdentity();
  const tenantId = identity.data?.tenantId ?? null;
  const counts = useClientSiteCounts(tenantId);
  const months = useStatisticsMonths(tenantId);
  const searches = useClientSearchMonths(tenantId);
  const connections = useAnalyticsConnections(tenantId);
  const [search, setSearch] = useSearchParams();

  const now = new Date();
  const choices = pickerMonths(now);
  const asked = search.get("month");
  const month = asked && choices.includes(asked) ? asked : choices[0];

  const picker = (
    <label className="s-picker">
      <span>Månad</span>
      <select
        value={month}
        data-testid="stat-month"
        onChange={(event) => setSearch(event.target.value === choices[0] ? {} : { month: event.target.value }, { replace: true })}
      >
        {choices.map((choice) => (
          <option key={choice} value={choice}>
            {monthWords(choice)}
          </option>
        ))}
      </select>
    </label>
  );

  const page = (content: ReactNode, withPicker = false) => (
    <div className="k-page">
      <ClientHead title="Statistik" actions={withPicker ? picker : undefined} />
      {content}
    </div>
  );
  const stateBox = (state: ReactNode) => <section className="panel">{state}</section>;

  if (identity.isLoading || counts.isLoading || months.isLoading || searches.isLoading || connections.isLoading) {
    return page(stateBox(<BoxState kind="loading" title="Hämtar statistiken" />));
  }

  if (!tenantId) {
    return page(stateBox(<BoxState kind="denied" title="Den här vyn är inte din" body="Kontot hör inte till någon kund i portalen." />));
  }

  if (counts.isError || months.isError || searches.isError || connections.isError) {
    return page(
      stateBox(
        <BoxState
          kind="error"
          title="Statistiken gick inte att hämta"
          body="Inget har ändrats. Försök igen, och hör av er i chatten om det upprepas."
          onRetry={() => {
            void counts.refetch();
            void months.refetch();
            void searches.refetch();
            void connections.refetch();
          }}
        />
      )
    );
  }

  const sites = siteCounts(counts.data ?? []);

  if (sites.length === 0) {
    return page(stateBox(<BoxState kind="empty" title="Ingen webbplats med förvaltning" body="Statistiken finns för webbplatser vi förvaltar." />));
  }

  return page(
    <div className="stack">
      {sites.map((site) => (
        <SiteSection
          key={site.serviceId}
          site={site}
          month={month}
          months={months.data ?? []}
          searches={searches.data ?? []}
          connectionId={(connections.data ?? []).find((connection) => connection.service_id === site.serviceId)?.id ?? null}
          now={now}
        />
      ))}
    </div>,
    true
  );
}
